import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { scrollToId } from "./scroll";

const TOPO_IMG =
  "https://images.unsplash.com/photo-1768916321622-6418c1af5de5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHx0b3BvZ3JhcGhpYyUyMG1hcCUyMGRhcmt8ZW58MHx8fHwxNzg2OTk0OTAyfDA&ixlib=rb-4.1.0&q=85";

const TAGLINE = "> TACTICAL STEALTH OPERATION. NO ALARMS. NO TRACE. NO WITNESSES.";

export const Hero = () => {
  const [typed, setTyped] = useState("");
  const [startTyping, setStartTyping] = useState(false);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 170]);
  const radarY = useTransform(scrollY, [0, 900], [0, -90]);

  useEffect(() => {
    const t = setTimeout(() => setStartTyping(true), 1150);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!startTyping) return;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped(TAGLINE.slice(0, i));
      if (i >= TAGLINE.length) clearInterval(t);
    }, 30);
    return () => clearInterval(t);
  }, [startTyping]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={TOPO_IMG} alt="" className="h-full w-full object-cover opacity-25 saturate-0" />
        <div className="absolute inset-0 bg-[#0A0E12]/70" />
      </motion.div>
      <div className="grid-bg absolute inset-0 opacity-40" />

      <motion.div
        style={{ y: radarY }}
        className="absolute right-[-150px] top-[10%] hidden lg:block"
        aria-hidden
      >
        <div className="relative h-[430px] w-[430px] rounded-full border border-line">
          <div className="absolute inset-8 rounded-full border border-line" />
          <div className="absolute inset-20 rounded-full border border-line" />
          <div className="absolute inset-32 rounded-full border border-signal/30" />
          <div className="radar-sweep absolute inset-0 rounded-full" />
          <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 bg-signal" />
          <div className="absolute left-[32%] top-[36%] h-1 w-1 animate-ping bg-signal/70" />
        </div>
      </motion.div>

      <div className="relative z-10 w-full max-w-[1400px] px-6 pb-24 pt-40 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.15 }}
          className="font-mono text-[11px] font-bold tracking-[0.35em] text-signal"
        >
          [ EYES ONLY // CLEARANCE LEVEL 5 ]
        </motion.p>

        <h1 className="mt-6 font-mono font-extrabold uppercase leading-[0.88] tracking-tight">
          {["PROJECT", "ECLIPSE"].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-6xl sm:text-8xl lg:text-[10rem] ${
                  i === 1 ? "text-signal" : "text-[#F8FAFC]"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <p
          data-testid="hero-tagline"
          className="terminal-caret mt-8 min-h-[1.5rem] font-mono text-xs tracking-wider text-slate-300 md:text-sm"
        >
          {typed}
        </p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 2.2 }}
          className="mt-10 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[10px] tracking-[0.25em] text-slate-500"
        >
          <span>
            PROTOCOL: <span className="text-slate-300">ZERO</span>
          </span>
          <span>
            STATUS: <span className="text-signal">ARMED</span>
          </span>
          <span>
            BUILD: <span className="text-slate-300">0.9.7-CLASSIFIED</span>
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 2.5 }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="mission-access-cta"
            onClick={() => scrollToId("access")}
            className="clip-angle border border-signal px-10 py-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-signal transition-colors hover:bg-signal hover:text-[#0A0E12]"
          >
            Mission Access
          </button>
          <button
            data-testid="hero-decrypt-btn"
            onClick={() => scrollToId("intel")}
            className="font-mono text-[11px] tracking-[0.25em] text-slate-500 transition-colors hover:text-signal"
          >
            DECRYPT INTEL &gt;&gt;
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 3 }}
          className="mt-16 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-slate-600"
        >
          <ChevronDown className="h-3.5 w-3.5 animate-bounce text-signal" />
          SCROLL // DECRYPT FILE
        </motion.div>
      </div>
    </section>
  );
};
